import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, Search, User, Car, Calendar, Phone, AlertTriangle, History } from 'lucide-react';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import VehicleModal from './vehicles/VehicleModal';
import VehicleDetailsModal from './vehicles/VehicleDetailsModal';
import RegisterVehicle from './RegisterVehicle';
import { formatDate } from '../utils/dateFormat';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

const VehicleMaster = () => {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('vehicles');
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showRepairHistory, setShowRepairHistory] = useState(false);
  const [repairHistory, setRepairHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pages: 1,
    total: 0,
    limit: 10
  });
  const [filters, setFilters] = useState({
    search: '',
    make: '',
    customerId: '',
    status: '',
    page: 1
  });

  useEffect(() => {
    fetchBrands();
    fetchCustomers();
  }, []);
  
  useEffect(() => {
    if (brands.length > 0) {
      fetchVehicles();
    }
  }, [filters, brands]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.make) params.append('make', filters.make);
      if (filters.customerId) params.append('customerId', filters.customerId);
      if (filters.status) params.append('status', filters.status);
      params.append('page', filters.page);
      params.append('limit', pagination.limit);
      
      const response = await axios.get(`/api/vehicles?${params}`);
      const vehiclesWithLogos = (response.data.vehicles || []).map(vehicle => {
        const brand = brands.find(b => b.name === vehicle.make);
        return {
          ...vehicle,
          brandLogo: brand?.logoUrl
        };
      });
      setVehicles(vehiclesWithLogos);
      setPagination(response.data.pagination || pagination);
    } catch (error) {
      toast.error('Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/customers');
      setCustomers(response.data.customers || []);
    } catch (error) {
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get('/api/vehicles/brands');
      setBrands(response.data || []);
    } catch (error) {
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowModal(true);
  };

  const handleViewDetails = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDetailsModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this vehicle? This action cannot be undone.')) {
      try {
        await axios.delete(`/api/vehicles/${id}`);
        toast.success('Vehicle deleted successfully');
        fetchVehicles();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to delete vehicle');
      }
    }
  };

  const handleSearch = () => {
    setFilters({ ...filters, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const getCustomerName = (customerId) => {
    if (!customerId) return 'No Customer Assigned';
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown Customer';
  };

  const getCustomerContact = (customerId) => {
    if (!customerId) return null;
    const customer = customers.find(c => c.id === customerId);
    return customer ? (customer.contact || customer.phone) : null;
  };



  const _getUniqueValues = (field) => {
    if (field === 'make') {
      return brands.map(b => b.name).sort();
    }
    const values = vehicles.map(v => v[field]).filter(Boolean);
    return [...new Set(values)].sort();
  };

  const fetchRepairHistory = async (vehicle) => {
    try {
      setHistoryLoading(true);
      const response = await axios.get(`/api/job-cards/vehicle/${vehicle.id}`);
      setRepairHistory(response.data.jobCards || []);
      setSelectedVehicle(vehicle);
      setShowRepairHistory(true);
    } catch (error) {
      toast.error('Failed to fetch repair history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const RepairHistoryModal = () => {
    if (!showRepairHistory || !selectedVehicle) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div className="flex justify-between items-center p-6 border-b">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Repair History</h3>
              <p className="text-sm text-gray-600">
                {selectedVehicle.make} {selectedVehicle.model} - {selectedVehicle.plateNumber || selectedVehicle.vin}
              </p>
            </div>
            <button
              onClick={() => setShowRepairHistory(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[70vh]">
            {historyLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading repair history...</p>
              </div>
            ) : repairHistory.length === 0 ? (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">No Repair History</h4>
                <p className="text-gray-500">This vehicle has no recorded repairs or services.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {repairHistory.map((jobCard) => (
                  <div key={jobCard.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">Job Card #{jobCard.jobCardNumber}</h4>
                        <p className="text-sm text-gray-600">{jobCard.description || 'No description'}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        jobCard.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                        jobCard.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                        jobCard.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {jobCard.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Date:</span> {new Date(jobCard.createdAt).toLocaleDateString()}
                      </div>
                      <div>
                        <span className="font-medium">Mileage:</span> {jobCard.mileage || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Technician:</span> {jobCard.technician?.name || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Total Cost:</span> ${jobCard.totalCost || '0.00'}
                      </div>
                    </div>
                    {jobCard.services && jobCard.services.length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm font-medium text-gray-700">Services:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {jobCard.services.map((service, index) => (
                            <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              {service.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vehicle Master</h1>
          <p className="text-sm text-gray-600 mt-1">Manage vehicles, brands and models</p>
        </div>
        {activeTab === 'vehicles' && hasPermission('vehicles.create') && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Vehicle
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-0">
        <button onClick={() => setActiveTab('vehicles')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'vehicles' ? 'bg-white border border-b-white text-blue-600 -mb-px' : 'text-gray-600 hover:text-gray-900'}`}>
          🚗 Vehicles
        </button>
        <button onClick={() => setActiveTab('brands')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'brands' ? 'bg-white border border-b-white text-blue-600 -mb-px' : 'text-gray-600 hover:text-gray-900'}`}>
          🏷️ Brands & Models
        </button>
      </div>

      {/* Brands & Models Tab */}
      {activeTab === 'brands' && <RegisterVehicle />}

      {/* Vehicles Tab */}
      {activeTab === 'vehicles' && (<>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by make, model, or plate..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filters.make}
            onChange={(e) => setFilters({ ...filters, make: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Makes</option>
            {brands.map(brand => (
              <option key={brand.id} value={brand.name}>{brand.name}</option>
            ))}
          </select>
          <select
            value={filters.customerId}
            onChange={(e) => setFilters({ ...filters, customerId: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Customers</option>
            {customers.map(customer => (
              <option key={customer.id} value={customer.id}>{customer.name}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="MAINTENANCE">Maintenance</option>
            <option value="SOLD">Sold</option>
          </select>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
          >
            <Search className="w-4 h-4 mr-2" />
            Search
          </button>
          <button
            onClick={() => {
              setFilters({ search: '', make: '', customerId: '', status: '', page: 1 });
            }}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Vehicles List */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Vehicles ({pagination.total})
          </h2>
          <div className="text-sm text-gray-500">
            Page {pagination.currentPage} of {pagination.pages}
          </div>
        </div>
        {loading ? (
          <div className="p-6">
            <div className="animate-pulse space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="p-12 text-center">
            <Car className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles found</h3>
            <p className="text-gray-500 mb-4">Get started by adding your first vehicle.</p>
            {hasPermission('vehicles.create') && (
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Vehicle
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="divide-y">
              {vehicles.map((vehicle) => (
                <div key={vehicle.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-14 h-14 rounded-lg flex items-center justify-center">
                        {vehicle.brandLogo ? (
                          <img 
                            src={`${(window.ENV && window.ENV.REACT_APP_API_URL) || process.env.REACT_APP_API_URL || 'http://localhost:5000'}${vehicle.brandLogo}`} 
                            alt={`${vehicle.make} logo`}
                            className="w-12 h-12 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <Car className={`w-6 h-6 text-blue-600 ${vehicle.brandLogo ? 'hidden' : ''}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-gray-900 text-lg">
                              {vehicle.make} {vehicle.model}
                              {vehicle.year && <span className="text-gray-500 font-normal"> ({vehicle.year})</span>}
                            </h3>
                            {vehicle.plateNumber && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-mono">
                                {vehicle.plateNumber}
                              </span>
                            )}
                          </div>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            vehicle.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                            vehicle.status === 'INACTIVE' ? 'bg-gray-100 text-gray-800' :
                            vehicle.status === 'MAINTENANCE' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {vehicle.status || 'ACTIVE'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500 mt-2">
                          <div className="space-y-1">
                            <div className="flex items-center">
                              <User className="w-4 h-4 mr-1" />
                              <span>{getCustomerName(vehicle.customerId)}</span>
                              {getCustomerContact(vehicle.customerId) && (
                                <>
                                  <span className="mx-1">•</span>
                                  <Phone className="w-3 h-3 mr-1" />
                                  <span>{getCustomerContact(vehicle.customerId)}</span>
                                </>
                              )}
                            </div>
                            {vehicle.color && (
                              <div className="flex items-center">
                                <span className="w-3 h-3 rounded-full border mr-2" style={{backgroundColor: vehicle.color.toLowerCase()}}></span>
                                <span>{vehicle.color}</span>
                              </div>
                            )}
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              <span>Added {formatDate(vehicle.createdAt)}</span>
                            </div>
                            {vehicle.mileage && (
                              <div className="flex items-center">
                                <span className="text-xs mr-1">📊</span>
                                <span>{vehicle.mileage.toLocaleString()} km</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleViewDetails(vehicle)}
                        className="text-gray-600 hover:text-gray-900 p-2 rounded-md hover:bg-gray-100"
                        title="View Details"
                      >
                        <Search className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => fetchRepairHistory(vehicle)}
                        className="text-purple-600 hover:text-purple-900 p-2 rounded-md hover:bg-purple-50"
                        title="Repair History"
                      >
                        <History className="w-4 h-4" />
                      </button>
                      {hasPermission('vehicles.update') && (
                        <button
                          onClick={() => handleEdit(vehicle)}
                          className="text-blue-600 hover:text-blue-800 p-2 rounded-md hover:bg-blue-50"
                          title="Edit Vehicle"
                        >
                          ✏️
                        </button>
                      )}
                      {hasPermission('vehicles.delete') && (
                        <button
                          onClick={() => handleDelete(vehicle.id)}
                          className="text-red-600 hover:text-red-800 p-2 rounded-md hover:bg-red-50"
                          title="Delete Vehicle"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.total)} of {pagination.total} vehicles
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 border rounded-md text-sm ${
                        page === pagination.currentPage 
                          ? 'bg-blue-600 text-white border-blue-600' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.pages}
                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      </>)}

      {showModal && (
        <VehicleModal
          vehicle={editingVehicle}
          onClose={() => { 
            setShowModal(false); 
            setEditingVehicle(null); 
            fetchVehicles(); 
          }}
        />
      )}
      
      {showDetailsModal && selectedVehicle && (
        <VehicleDetailsModal
          vehicle={selectedVehicle}
          customer={customers.find(c => c.id === selectedVehicle.customerId)}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedVehicle(null);
          }}
          onEdit={() => {
            setShowDetailsModal(false);
            handleEdit(selectedVehicle);
          }}
        />
      )}
      
      <RepairHistoryModal />
    </div>
  );
};



export default VehicleMaster;
